import {
	Box,
	createStyles,
	Flex,
	getDefaultZIndex,
	getStylesRef,
	Navbar,
	ScrollArea,
	Space,
	Stack,
} from '@mantine/core';
import { useWindowResize } from '@repo/common/hooks/useWindowResize';
import { Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router';
import { useAuthUser } from '../../../api';
import { useUserPreferences } from '../../../api/hooks/user/useUserPreferences';
import { useSidebarBreakpoints } from '../../../hooks/useRightSidebarPortalBehavior';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { SettingsSideBarLinks } from '../../Settings/SettingsSideBarLinks';
import {
	COLLAPSED_NAV_SIDEBAR_WIDTH,
	EXPANDED_NAV_MAX_SIDEBAR_WIDTH,
} from '../constants';
import ResizeSideBar from '../ResizeSideBar';
import { SearchButton } from './SearchButton';
import { SideBarCreateMenu } from './SideBarCreateMenu';
import SideBarFooter from './SideBarFooter';
import SideBarHeader from './SideBarHeader';
import SideBarTeamsLinks from './SideBarTeamsLinks';
import SideBarWorkspaceLinks from './SideBarWorkspaceLinks';
import { sideBarStore } from './store';
import { WorkspaceSelector } from './WorkspaceSelector';

interface SideBarStyleProps {
	collapsed: boolean;
	width: number;
	mobileBreakpointQuery: string;
}

const useStyles = createStyles(
	(theme, { collapsed, width, mobileBreakpointQuery }: SideBarStyleProps) => ({
		navbar: {
			ref: getStylesRef('appSideBar'),
			minWidth: COLLAPSED_NAV_SIDEBAR_WIDTH,
			maxWidth: `min(${EXPANDED_NAV_MAX_SIDEBAR_WIDTH}, 90dvw)`,
			width,
			height: '100dvh',
			paddingTop: theme.other.space[3],
			paddingBottom: 0,
			paddingLeft: theme.other.space[3],
			paddingRight: theme.other.space[1],
			backgroundColor: theme.other.getColor('surface/tertiary/default'),
			borderRightWidth: theme.other.space[0.25],
			borderRightColor: theme.other.getColor(`border/secondary/default`),
			transform: collapsed ? 'translateX(-100%)' : 'translateX(0)',
			transition: 'transform 0.3s ease-in-out',

			[`&:has(.${getStylesRef('resizeHandle')}:hover)`]: {
				borderRightColor: theme.other.getColor(`border/secondary/hover`),
			},
			[`&:has(.${getStylesRef('resizeHandle')}:active)`]: {
				borderRightColor: theme.other.getColor(`border/secondary/active`),
			},

			[theme.fn.smallerThan('md')]: {
				position: 'fixed',
				top: 0,
				left: 0,
				zIndex: getDefaultZIndex('app') + 1,
				boxShadow: theme.shadows.lg,
			},

			'@media print': {
				display: 'none',
			},
		},
		backToAppButton: {
			display: 'flex',
			alignItems: 'center',
			gap: theme.spacing['3xs'],
			textDecoration: 'none',
			borderRadius: theme.other.borderRadius.sm,
			height: theme.other.space[7],
			width: '100%',
			cursor: 'pointer',
			color: theme.other.getColor('text/primary/default'),
			backgroundColor: 'transparent',

			':hover': {
				backgroundColor: theme.other.getColor('fill/transparent/hover'),
			},
			':active': {
				backgroundColor: theme.other.getColor('fill/transparent/active'),
			},
		},
		topRowWrapper: {
			display: 'flex',
			flex: '0 1 auto',
			flexDirection: 'row',
			justifyContent: 'space-between',
			gap: '0',
			alignItems: 'center',
			minWidth: 0,
		},
		scrollbar: {
			display: 'block',
			justifyContent: 'unset',
			'.mantine-ScrollArea-scrollbar': {
				backgroundColor: 'transparent',
			},
			'.mantine-ScrollArea-viewport': {
				padding: 0,
				paddingRight: theme.other.space[2],
				width: 'unset',
			},
		},
		resizeHandle: {
			ref: getStylesRef('resizeHandle'),
			display: 'block',
		},
		overlay: {
			display: 'none',
			position: 'fixed',
			top: 0,
			left: 0,
			width: '100vw',
			height: '100dvh',
			backgroundColor: 'rgba(0, 0, 0, 0.24)',
			zIndex: getDefaultZIndex('app'),

			[`@media ${mobileBreakpointQuery}`]: {
				display: 'block',
			},
		},
	})
);

function SideBar() {
	const { autoCollapseBreakpoint, mobileBreakpointQuery } =
		useSidebarBreakpoints();
	const { classes, theme } = useStyles({
		collapsed: !!sideBarStore.collapsed,
		width: sideBarStore.width,
		mobileBreakpointQuery,
	});
	const { settingsNav } = useFeatureFlags();
	const { isViewerUser, user } = useAuthUser();
	const { teamOrder, updateTeamOrder } = useUserPreferences();
	const location = useLocation();
	const isSettingsPage = location.pathname.startsWith('/settings/');

	useWindowResize(({ current, previous }) => {
		// Make sure that auto collapse happens before overlay is shown
		if (
			current.width < autoCollapseBreakpoint &&
			previous.width >= autoCollapseBreakpoint
		) {
			sideBarStore.setCollapsedByWindowResize(true);
		} else if (
			current.width >= autoCollapseBreakpoint &&
			previous.width < autoCollapseBreakpoint &&
			sideBarStore.autoCollapsedByWindowResize
		) {
			// Only re-open if it was previously collapsed by window resize
			sideBarStore.setCollapsedByWindowResize(false);
		}
	}, []);

	// forced by route definition
	if (!sideBarStore.showSideBar) {
		return null;
	}

	return (
		<>
			<Navbar className={classes.navbar} withBorder data-testid="sidebar">
				{(!settingsNav || !isSettingsPage) && (
					<Navbar.Section>
						<Stack spacing="sm" pr="xs">
							<Box className={classes.topRowWrapper}>
								<WorkspaceSelector />
								<Flex gap="xs" wrap="nowrap" direction="row">
									<SideBarCreateMenu isViewerUser={isViewerUser} />
									<SearchButton />
								</Flex>
							</Box>
						</Stack>
					</Navbar.Section>
				)}
				{(!settingsNav || !isSettingsPage) && <Space h="sm" />}
				{(!settingsNav || !isSettingsPage) && (
					<Navbar.Section>
						<SideBarHeader />
					</Navbar.Section>
				)}
				{isSettingsPage && settingsNav && (
					<Box mr="xs">
						<Link className={classes.backToAppButton} to="/">
							<Icon name="chevronLeft" />
							<Text fz="sm" weight="semibold">
								Back to app
							</Text>
						</Link>
					</Box>
				)}
				<Space h="sm" />
				<Navbar.Section
					className={classes.scrollbar}
					type="auto"
					component={ScrollArea}
					offsetScrollbars
					scrollbarSize={theme.other.space[2]}
					grow
				>
					{isSettingsPage && settingsNav ? (
						<SettingsSideBarLinks />
					) : (
						<>
							<SideBarTeamsLinks
								key={JSON.stringify(teamOrder)}
								teamOrder={teamOrder}
								updateTeamOrder={updateTeamOrder}
							/>
							<Space h="md" />
							<SideBarWorkspaceLinks />
						</>
					)}
					<Space h={theme.other.space[10] + theme.other.space[3]} />
				</Navbar.Section>
				<SideBarFooter />
				<ResizeSideBar className={classes.resizeHandle} />
			</Navbar>
			{!sideBarStore.collapsed && (
				<Box
					className={classes.overlay}
					onClick={() => sideBarStore.toggle()}
				/>
			)}
		</>
	);
}

const ObservedSideBar = observer(SideBar);
export default ObservedSideBar;
